import React from 'react';
import HeroSlider from '../components/HeroSlider';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

const Home = () => {
  const { t } = useLanguage();

  return (
    <>
      <SEO 
        title="SRE GLOBAL | Trading Platform, Insurance & Tourism | Yetkili Aracı Kurum & FCA | sreglobaltrade.com"
        description="SRE GLOBAL - Switzerland-based financial services. Yetkili aracı kurum. UK FCA regulated. Trading platform, insurance, tourism, and mutual funds at sreglobaltrade.com."
        keywords="SRE GLOBAL, Yetkili Aracı Kurum, sreglobaltrade, sreglobaltrade.com, SRE GLOBAL TRADING PLATFORM, SRE GLOBAL insurance, SRE GLOBAL Tourism, FCA license, Swiss Re, portfolio management, investment advisory"
        path="/"
      />
      <HeroSlider />

      {/* Client Tools Section */}
      <section className="py-16 bg-neutral-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h2 className="text-2xl font-semibold text-neutral-800 mb-4">
                {t.home.clientTools}
              </h2>
              <p className="text-neutral-600 mb-6 leading-relaxed">
                {t.home.clientToolsDesc}
              </p>
              <a href="/client-tools" className="inline-block px-6 py-3 border border-teal-600 text-teal-600 rounded-md hover:bg-teal-50 transition-colors">
                {t.home.exploreTools}
              </a>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="h-full flex items-center justify-center">
                <img
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&h=400&fit=crop"
                  alt="Client Tools"
                  className="w-full h-64 object-cover rounded-md"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Group Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <h3 className="text-sm font-semibold text-neutral-500 tracking-wider mb-4">
              {t.home.ourGroup}
            </h3>
            <p className="text-2xl text-neutral-700 leading-relaxed">
              {t.home.groupDesc}
            </p>
          </div>
        </div>
      </section>

      {/* Licenses & Regulation Section - SEO Optimized */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
              {t.home.licensesTitle}
            </h2>
            <p className="text-base md:text-lg text-neutral-700 leading-relaxed">
              {t.home.licensesSubtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-gradient-to-br from-red-50 to-white border-2 border-red-200 rounded-lg p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">🇹🇷</span>
                <h3 className="text-xl font-semibold text-neutral-900">{t.home.spkTitle}</h3>
              </div>
              <p className="text-neutral-700 leading-relaxed mb-3">
                {t.home.spkDesc}
              </p>
              <p className="text-sm text-neutral-600">
                {t.home.spkType}
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-lg p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">🇬🇧</span>
                <h3 className="text-xl font-semibold text-neutral-900">{t.home.fcaTitle}</h3>
              </div>
              <p className="text-neutral-700 leading-relaxed mb-3">
                {t.home.fcaDesc}
              </p>
              <p className="text-sm text-neutral-600">
                {t.home.fcaType}
              </p>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-neutral-600">
              {t.footer.licenseNote}
            </p>
          </div>
        </div>
      </section>

      {/* Our Business Section */}
      <section id="business" className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-semibold text-neutral-800 mb-12">
            {t.home.ourBusiness}
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Trading Platform */}
            <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <img
                src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=600&h=400&fit=crop"
                alt="Trading Platform"
                className="w-full h-64 object-cover"
              />
              <div className="p-8">
                <h3 className="text-2xl font-semibold text-neutral-800 mb-4">
                  {t.business.trading.title}
                </h3>
                <p className="text-neutral-600 mb-6 leading-relaxed">
                  {t.business.trading.desc}
                </p>
                <a href="/trading" className="inline-block px-6 py-2 border border-teal-600 text-teal-600 rounded-md hover:bg-teal-50 transition-colors">
                  {t.business.trading.discover}
                </a>
              </div>
            </div>

            {/* Insurance */}
            <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <img
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop"
                alt="Insurance Services"
                className="w-full h-64 object-cover"
              />
              <div className="p-8">
                <h3 className="text-2xl font-semibold text-neutral-800 mb-4">
                  {t.business.insurance.title}
                </h3>
                <p className="text-neutral-600 mb-6 leading-relaxed">
                  {t.business.insurance.desc}
                </p>
                <a href="/insurance" className="inline-block px-6 py-2 border border-teal-600 text-teal-600 rounded-md hover:bg-teal-50 transition-colors">
                  {t.business.insurance.discover}
                </a>
              </div>
            </div>

            {/* Mutual Funds */}
            <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <img
                src="https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=600&h=400&fit=crop"
                alt="Mutual Funds"
                className="w-full h-64 object-cover"
              />
              <div className="p-8">
                <h3 className="text-2xl font-semibold text-neutral-800 mb-4">
                  {t.business.investment.title}
                </h3>
                <p className="text-neutral-600 mb-6 leading-relaxed">
                  {t.business.investment.desc}
                </p>
                <a href="/mutual-funds" className="inline-block px-6 py-2 border border-teal-600 text-teal-600 rounded-md hover:bg-teal-50 transition-colors">
                  {t.business.investment.discover}
                </a>
              </div>
            </div>

            {/* Tourism */}
            <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <img
                src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=600&h=400&fit=crop"
                alt="Tourism Services"
                className="w-full h-64 object-cover"
              />
              <div className="p-8">
                <h3 className="text-2xl font-semibold text-neutral-800 mb-4">
                  {t.business.tourism.title}
                </h3>
                <p className="text-neutral-600 mb-6 leading-relaxed">
                  {t.business.tourism.desc}
                </p>
                <a href="/tourism" className="inline-block px-6 py-2 border border-teal-600 text-teal-600 rounded-md hover:bg-teal-50 transition-colors">
                  {t.business.tourism.discover}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;